import React, { useContext, useEffect, useState } from 'react'
import API from '../../../../../services/API'
import { GlobalContext } from '../../../../../App'
import { getWorkerPaymentType } from '../../../../../helpers/workerPaymentType'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import SaveIcon from '@mui/icons-material/Save'

const monthOptions = [
  ['January', 1],
  ['February', 2],
  ['March', 3],
  ['April', 4],
  ['May', 5],
  ['June', 6],
  ['July', 7],
  ['August', 8],
  ['September', 9],
  ['October', 10],
  ['November', 11],
  ['December', 12],
]

const employmentTypeOptions = ['PAYE', 'Invoice']

// The select needs an empty string (not 'Unknown') when the type is unset
const getPaymentType = (employmentType = '') => {
  const paymentType = getWorkerPaymentType(employmentType)
  return paymentType === 'Unknown' ? '' : paymentType
}

const PayrollTab = ({ employee, loadEmployee }) => {
  const { setToast } = useContext(GlobalContext)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(employee?.employee?.payroll ?? {})

  useEffect(() => {
    setForm(employee?.employee?.payroll ?? {})
  }, [employee])

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const savePayroll = () => {
    const paymentType = getPaymentType(form.employmentType)
    setSaving(true)
    API.put('/employee', {
      updates: {
        _id: employee._id,
        firstName: employee.firstName,
        lastName: employee.lastName,
        photograph: employee.photograph,
        email: employee.email,
        prefix: employee.prefix,
        permittedBaseLocations: employee.permittedBaseLocations,
        employee: {
          ...employee.employee,
          payroll: {
            ...employee.employee?.payroll,
            ...form,
            employmentType: paymentType || form.employmentType,
            contractedHoursPerWeek:
              form.contractedHoursPerWeek === ''
                ? undefined
                : Number(form.contractedHoursPerWeek ?? 0),
            annualLeaveEntitlementDays:
              form.annualLeaveEntitlementDays === ''
                ? undefined
                : Number(form.annualLeaveEntitlementDays ?? 28),
            annualLeaveYearStartMonth:
              form.annualLeaveYearStartMonth === ''
                ? undefined
                : Number(form.annualLeaveYearStartMonth ?? 1),
            annualLeaveWorkingHoursPerDay:
              form.annualLeaveWorkingHoursPerDay === ''
                ? undefined
                : Number(form.annualLeaveWorkingHoursPerDay ?? 7.5),
          },
        },
      },
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Payroll profile updated' })
          loadEmployee?.()
        } else {
          setToast({
            severity: 'error',
            message:
              res.data?.error ??
              'Unable to update the payroll profile. Please try again.',
          })
        }
      })
      .catch(() =>
        setToast({
          severity: 'error',
          message:
            'Unable to update the payroll profile. Please try again.',
        }),
      )
      .finally(() => setSaving(false))
  }

  const paymentType = getPaymentType(form.employmentType)

  return (
    <Stack gap={2} mt={1} p={1}>
      <Alert severity="info" variant="outlined">
        These fields control AmbuNet payroll categorisation and holiday balance
        calculations. They do not calculate statutory payroll deductions.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack gap={2}>
          <Stack>
            <Typography variant="title3">Employment and Pay</Typography>
            <Typography variant="body2" color="text.secondary">
              Worker type is used to split pay-run totals between PAYE-style
              payments and invoice/remittance workers.
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
            <TextField
              select
              label="Employment Type"
              value={paymentType}
              onChange={(e) => updateField('employmentType', e.target.value)}
              fullWidth
            >
              {employmentTypeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Contracted Hours Per Week"
              type="number"
              value={form.contractedHoursPerWeek ?? ''}
              onChange={(e) => updateField('contractedHoursPerWeek', e.target.value)}
              fullWidth
            />
          </Stack>
        </Stack>
      </Paper>

      {paymentType === 'PAYE' && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack gap={2}>
            <Stack>
              <Typography variant="title3">Holiday Entitlement</Typography>
              <Typography variant="body2" color="text.secondary">
                These values drive the staff member's My Availability holiday
                balance.
              </Typography>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
              <TextField
                label="Annual Leave Entitlement"
                type="number"
                value={form.annualLeaveEntitlementDays ?? 28}
                onChange={(e) =>
                  updateField('annualLeaveEntitlementDays', e.target.value)
                }
                helperText="Days per leave year"
                fullWidth
              />
              <TextField
                select
                label="Leave Year Starts"
                value={form.annualLeaveYearStartMonth ?? 1}
                onChange={(e) =>
                  updateField('annualLeaveYearStartMonth', e.target.value)
                }
                fullWidth
              >
                {monthOptions.map(([label, value]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Working Hours Per Day"
                type="number"
                value={form.annualLeaveWorkingHoursPerDay ?? 7.5}
                onChange={(e) =>
                  updateField('annualLeaveWorkingHoursPerDay', e.target.value)
                }
                fullWidth
              />
            </Stack>
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack gap={2}>
          <Typography variant="title3">BACS</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
            <TextField
              label="Name on Account"
              value={form.bacsName ?? ''}
              onChange={(e) => updateField('bacsName', e.target.value)}
              fullWidth
            />
            <TextField
              label="Sort Code"
              value={form.bacsSortCode ?? ''}
              onChange={(e) => updateField('bacsSortCode', e.target.value)}
              fullWidth
            />
            <TextField
              label="Account Number"
              value={form.bacsAccountNumber ?? ''}
              onChange={(e) => updateField('bacsAccountNumber', e.target.value)}
              fullWidth
            />
          </Stack>
        </Stack>
      </Paper>

      {paymentType === 'Invoice' && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack gap={2}>
            <Stack>
              <Typography variant="title3">Invoicing</Typography>
              <Typography variant="body2" color="text.secondary">
                A VAT-registered worker's invoices add standard-rate VAT on top
                of their pay. Leave blank if the worker is not VAT registered.
              </Typography>
            </Stack>
            <TextField
              label="VAT Registration Number"
              value={form.vatNumber ?? ''}
              onChange={(e) => updateField('vatNumber', e.target.value)}
              sx={{ maxWidth: { md: 320 } }}
              fullWidth
            />
          </Stack>
        </Paper>
      )}

      <Stack alignItems="flex-end">
        <Button
          startIcon={<SaveIcon />}
          variant="contained"
          disabled={saving}
          onClick={savePayroll}
        >
          Save Payroll Profile
        </Button>
      </Stack>
    </Stack>
  )
}

export default PayrollTab
